<template>
		<Drawer :title="$t('Set theme period')" initialWidth="2xl" @requestUnmount="hide">

			<template #header>
				<div class="font-semibold text-md">
					{{ $t('Set theme period') }}: {{ vModelEditableCopy.dateRange.start.toLocaleString({ day: "numeric", month: "numeric" }) }} - {{ vModelEditableCopy.dateRange.end.minus({ hours: 2 }).toLocaleString({ day: "numeric", month: "numeric" }) }}
				</div>
			</template>

			<template #default="{ hide, resizeTo }">

				<form @submit.prevent="save" class="pt-4">

					<div class="drawer-content grid grid-cols-6 gap-4">

						<!-- <div class="col-span-6">
							<label v-text="$t('Period')" class="text-sm"></label>
							<div class="">
								<DateRangeSelector ref="datepicker" v-model="vModelEditableCopy.dateRange" class="input input-bordered bg-base-100"   />
								{{ vModelEditableCopy.dateRange.start.toFormat('dd/MM/yyyy') }} - {{ vModelEditableCopy.dateRange.end.minus({ hours: 2 }).toFormat('dd/MM/yyyy') }}
							</div>
						</div> -->

						<div class="col-span-6">

							<div class="flex justify-between items-center">
								<label v-text="$t('Name')" class="text-sm" for="themeNameFld" :class="[invalidName || themesStore.errors?.fields.name ? 'text-error-content' : '']"></label>
								<label class="text-sm px-1 font-semibold pb-1" :class="[remainingNameCharacters < 0? 'text-danger' : '']">
									{{ remainingNameCharacters }}
									<div class="radial-progress bg-base-100 ring-inset ring-1 ring-base-300" style="--size:1.1rem;--thickness:.15rem" :style="{ '--value': nameProgressValue }" role="progressbar"></div>
								</label>
							</div>
							<input type="text" id="themeNameFld" ref="themeNameFld" autofocus autocomplete="off" spellcheck="false" v-model="vModelEditableCopy.name" class="input w-full" :class="[invalidName || themesStore.errors?.fields.name? 'border-red-500' : '']" />
							<span v-if="invalidName" class="text-red-600 text-xs">
								{{ $t('#error: field required', {fieldName: $t('Name')}) }}
							</span>

						</div>

						<div class="col-span-6">
							<label v-text="$t('Description')" class="text-sm" for="themeDescriptionFld"></label>
							<textarea id="themeDescriptionFld" ref="themeDescriptionFld" v-model="vModelEditableCopy.description" class="textarea w-full h-28 leading-5" :class="[invalidName? 'border-red-500' : '']">
							</textarea>

							<span v-if="themesStore.errors?.fields.description" class="text-red-600 text-xs">
								{{ themesStore.errors?.fields.description }}
							</span>
						</div>

						<div v-if="organisationsStore.currentOrganisation.curriculum" class="col-span-6 mt-4 pt-4 border-t">
							<label v-text="$t('Week goal', 2)" class="text-sm"></label>
							<div class="mt-4">
								<GoalSelectorBar
									context="themeWeek"
									scope="school"
									:selectedGoals="vModelEditableCopy.goals"
									:launchPending="goalLaunchPending"
									@openCurriculum="launchGoalSource({ curriculum: $event })"
									@openCollection="launchGoalSource($event)"
								/>
							</div>

							<div
								v-show="!vModelEditableCopy.goals?.length"
								class="text-base-content-light text-sm italic text-center my-6"
							>
								{{ $t("No goals selected") }}
							</div>

							<GoalsField
								:curriculum="organisationsStore.currentOrganisation.curriculum"
								:selectedGoals="vModelEditableCopy.goals"
								context="theme"
								@goalSelected="addGoal"
								@goalUnselected="removeGoal"
								@goalMuteToggled="toggleGoalMuted"
							/>

						</div>
					</div>

					<div class="drawer-action-bar gap-2" :class="[themePeriod? 'justify-between' : 'justify-end']">
						<ActionConfirm position="top-start" class="w-80 text-danger font-bold" v-slot="confirmationDialog">
							<button v-if="themePeriod.id" type="button" class="btn" :class="[saving? 'btn-disabled':'btn-outline btn-danger']" @click="handleDeleteIntent(confirmationDialog)">
								<span v-show="deleting" class="loading loading-spinner"></span>
								<TrashIcon class="w-5 h-5"/>
							</button>
						</ActionConfirm>

						<div class="flex gap-2">
							<button type="button" class="btn btn-ghost" @click="hide" :class="[saving || deleting? 'hidden':'']">
								{{ $t('Cancel') }}
							</button>
							<button type="submit" class="btn" :class="[deleting? 'btn-disabled':'btn-primary']">
								<span v-show="saving" class="loading loading-spinner"></span>
								{{ $t('Save') }}
							</button>
						</div>
					</div>

				</form>

			</template>

		</Drawer>

		<component
			v-if="activeGoalLaunch?.kind === 'external'"
			:is="activeGoalLaunch.component"
			context="theme"
			:selectedGoals="vModelEditableCopy.goals"
			@goalSelected="addGoal"
			@goalUnselected="removeGoal"
			@opened="goalLaunchPending = false"
			@requestUnmount="activeGoalLaunch = null; goalLaunchPending = false"
		/>

		<GoalSelector
			v-else-if="activeGoalLaunch?.kind === 'drawer'"
			:curriculum="activeGoalLaunch.curriculum"
			:fixedSource="activeGoalLaunch.fixedSource"
			:selectedGoals="vModelEditableCopy.goals"
			@goalSelected="addGoal"
			@goalUnselected="removeGoal"
			@requestUnmount="activeGoalLaunch = null"
		/>
</template>

<script lang="ts">
import { Theme } from "@/models/Theme.model";

import Drawer from "@/components/ui/Drawer.v2.vue";
import GoalSelector from "@/components/GoalSelector.vue";
import GoalsField from "@/components/GoalsField.vue";
import GoalSelectorBar from "@/components/GoalSelectorBar.vue";
import ActionConfirm from "@/components/ui/ActionConfirm.vue";
import DateRangeSelector from "@/components/ui/DateRangeSelector.vue";

import { TrashIcon } from "@heroicons/vue/24/outline";
import {mapStores} from "pinia";
import {useThemesStore} from "@/stores/Themes.store";
import {useOrganisationsStore} from "@/stores/Organisations.store";
import {Goal} from "@/models/Goal.model";
import {UnsavedChanges} from "@/utils/UnsavedChanges.util";
import {useGoalsStore} from "@/stores/Goals.store.ts";
import { GoalPickerLaunchDescriptor, GoalPickerLaunchSource, resolveGoalPickerLaunch } from "@/goals/utils/GoalPickerLaunch.util";


export default {

	props: {
		themePeriod: {
			type: Theme,
			required: false,
		},

		nameMaxLength: {
			type: Number,
			default: 80,
		}
	},

	components: {
		Drawer,
		GoalSelector,
		GoalsField,
		GoalSelectorBar,
		ActionConfirm,
		DateRangeSelector,
		TrashIcon,
	},

	data() {
		return {
			saving: false,
			deleting: false,
			vModelEditableCopy: null,
			activeGoalLaunch: null as GoalPickerLaunchDescriptor | null,
			// True from the click until the external picker reports `opened` (or gives up
			// and unmounts). Only external launches set it — drawer launches open instantly.
			goalLaunchPending: false,
			dateRange: null,
			invalidName: false,
		}
	},

	beforeMount() {
		this.vModelEditableCopy = this.themePeriod.clone();
	},

	mounted() {
		UnsavedChanges.observeModel(this.vModelEditableCopy, this.themePeriod);
	},

	unmounted() {
		UnsavedChanges.unobserveModel();
	},

	computed: {
		...mapStores(useThemesStore, useOrganisationsStore, useGoalsStore),

		remainingNameCharacters(): number {
			if(!this.vModelEditableCopy.name) {
				return this.nameMaxLength;
			}
			return this.nameMaxLength - this.vModelEditableCopy.name.length;
		},

		nameProgressValue(): number {
			if(!this.vModelEditableCopy.name) {
				return 0;
			}
			return Math.round((this.vModelEditableCopy.name.length / this.nameMaxLength) * 100);
		},
	},

	methods: {
		hide() {
			this.themesStore.clearErrors();
			this.$emit('requestClose');
		},

		async save() {
			this.invalidName = false;

			if (!this.vModelEditableCopy.name) {
				this.themesStore.clearErrors();
				this.invalidName = true;
				this.$refs.themeNameFld.focus();
				return;
			}

			this.saving = true;

			try {
				await this.themesStore.store(this.vModelEditableCopy);
			} catch (e) {
				this.saving = false;
				return;
			}

			this.saving = false;
			this.hide();
		},

		async handleDeleteIntent(confirmationDialog) {
			confirmationDialog.setMessage( this.$t('Are you sure?') );
			const confirmed = await confirmationDialog.show();
			if(!confirmed) {
				return;
			}

			this.deleting = true;

			await this.themesStore.delete(this.vModelEditableCopy);
			this.hide();
		},

		launchGoalSource(source: GoalPickerLaunchSource) {
			try {
				const descriptor = resolveGoalPickerLaunch(source);
				this.goalLaunchPending = descriptor.kind === 'external';
				this.activeGoalLaunch = descriptor;
			} catch (e) {
				console.error('Could not launch goal selector', e);
				this.$notify({ text: this.$t('An error occurred'), type: 'error' });
			}
		},

		addGoal(goal: Goal) {
			const goals = this.vModelEditableCopy.goals;
			const existing = goals.find(
				// we use the goalstore to compare goals, as that knows how to compare goals
				// (do not use equals() as that compares the properties as well)
				(item) => this.goalsStore.representsSameModel(item, goal)
			);
			if (existing) {
				// Picked again: only the link state can change. Clone-and-replace so the
				// shared original instance is not mutated (see toggleGoalMuted).
				if (existing.muted !== goal.muted) {
					const updated = existing.clone();
					updated.muted = goal.muted;
					goals.splice(goals.indexOf(existing), 1, updated);
				}
				return;
			}
			goals.push(goal);
		},

		toggleGoalMuted(goal: Goal) {
			const goals = this.vModelEditableCopy.goals;
			const index = goals.indexOf(goal);
			if (index === -1) return;
			// Clone-and-replace: the Goal instance is shared by reference with the
			// original model (LessonPlan.clone() copies the array shallowly), so an
			// in-place mutation would edit the original too and blind the
			// unsaved-changes comparison. A fresh instance keeps the original intact.
			const toggled = goal.clone();
			toggled.muted = !goal.muted;
			goals.splice(index, 1, toggled);
		},

		removeGoal(goal: Goal) {
			// in case of a 'main goal', also remove any potential 'sub goals'
			if(goal.sourcePathId) {
				this.vModelEditableCopy.goals = this.vModelEditableCopy.goals.filter(
					(item) => item.sourceParentPath != goal.fullPath
				)
			}

			this.vModelEditableCopy.goals = this.vModelEditableCopy.goals.filter(
				// we use the goalstore to compare goals, as that knows how to compare goals
				// (do not use equals() as that compares the properties as well)
				(item) => !this.goalsStore.representsSameModel(item, goal)
			);
		},
	}
}

</script>
